
import { Link } from 'react-router-dom';

export default function UTEPCertificatesAndCTA() {
  const certificates = [
    {
      title: 'Certificate in Real Estate Finance',
      desc: 'A focused credential covering real estate valuation, mortgage analysis, investment underwriting, and market feasibility studies. Ideal for professionals seeking to enter or advance in the real estate industry without committing to a full degree program.',
    },
    {
      title: 'Certificate in Business Analytics',
      desc: 'Teaches data-driven decision making using tools such as SQL, Python, Tableau, and Excel. Students learn to collect, clean, analyze, and visualize business data to support strategic planning and operational improvement.',
    },
    {
      title: 'Certificate in Project Management',
      desc: 'Aligns with Project Management Institute (PMI) standards and prepares students for the PMP certification exam. Coursework covers project planning, scheduling, budgeting, risk management, and stakeholder communication.',
    },
    {
      title: 'Certificate in Digital Marketing',
      desc: 'Covers search engine optimization, pay-per-click advertising, social media marketing, email campaigns, and content strategy. Students gain hands-on experience managing real campaigns and analyzing performance metrics.',
    },
    {
      title: 'Certificate in Entrepreneurship',
      desc: 'Designed for aspiring business owners, this certificate covers opportunity recognition, business model development, lean startup methodology, financial planning, and pitch preparation. Students develop and refine their own venture concepts throughout the program.',
    },
    {
      title: 'Certificate in Supply Chain Management',
      desc: 'Addresses logistics, procurement, inventory management, and international trade compliance. Given El Paso\u2019s role as a major trade gateway, this certificate is particularly relevant for professionals in the region\u2019s import-export and manufacturing sectors.',
    },
  ];

  return (
    <>
      {/* Certificates Section */}
      <section className="mb-20">
        <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
          <span className="w-10 h-10 bg-[#D2691E] rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
            <i className="ri-file-list-3-line text-white text-lg"></i>
          </span>
          Professional Certificates
        </h3>
        <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-4xl">
          For working professionals who want to add specific competencies without pursuing a full degree, UTEP&apos;s College of Business Administration and its Extended University division offer a range of professional certificates. These programs can typically be completed in one to two semesters and are available in evening, weekend, and online formats.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {certificates.map((cert, i) => (
            <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 bg-[#FFF3E8] rounded-lg flex items-center justify-center mb-4">
                <i className="ri-medal-line text-[#D2691E] text-lg"></i>
              </div>
              <h4 className="text-base font-bold text-gray-900 mb-2">{cert.title}</h4>
              <p className="text-sm text-gray-600 leading-relaxed">{cert.desc}</p>
            </div>
          ))}
        </div>

        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
          <p>
            These certificates carry the weight of UTEP&apos;s AACSB-accredited College of Business Administration, giving holders a credential that is recognized and respected by employers throughout the region and beyond. Many certificate earners go on to pursue full degree programs at UTEP, applying their certificate coursework toward degree requirements and accelerating their time to graduation.
          </p>
          <p>
            In addition to formal certificates, UTEP&apos;s Extended University offers non-credit professional development workshops, seminars, and boot camps in areas such as leadership, negotiation, financial literacy, and small business management. These short-format programs are ideal for entrepreneurs and business owners who need targeted skills quickly and affordably.
          </p>
        </div>
      </section>

      {/* Continuing Education & Community */}
      <section className="mb-20">
        <h2 className="text-4xl font-bold text-gray-900 mb-6">Continuing Education &amp; Community Engagement</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-10">
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              UTEP&apos;s commitment to the El Paso community extends well beyond traditional degree programs. The university operates several centers and initiatives that directly support local businesses, aspiring entrepreneurs, and working professionals seeking to advance their careers.
            </p>
            <p>
              The <strong>Mike Loya Center for Innovation and Commerce</strong> serves as a hub for entrepreneurship education and startup support. The center hosts pitch competitions, mentorship programs, and networking events that connect student entrepreneurs with experienced business leaders and potential investors. Its programs are open to community members as well as enrolled students, making it a valuable resource for anyone in El Paso with a business idea.
            </p>
            <p>
              The <strong>UTEP Small Business Development Center (SBDC)</strong>, funded in part by the U.S. Small Business Administration, provides free one-on-one consulting to small business owners and aspiring entrepreneurs throughout the region. SBDC advisors help clients with business planning, financial analysis, marketing strategy, loan applications, and government contracting. The center has assisted thousands of El Paso businesses over the years, contributing to job creation and economic growth.
            </p>
            <p>
              UTEP also partners with the <strong>SCORE El Paso</strong> chapter, which pairs entrepreneurs with volunteer mentors who have decades of business experience. These mentoring relationships provide invaluable guidance on everything from startup logistics to scaling operations and navigating regulatory requirements.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden h-[500px]">
            <img
              src="https://public.readdy.ai/ai/img_res/5e12bba3e501f86bce82ae5e4ee87553.jpg"
              alt="UTEP Business Networking Event"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
          <p>
            For real estate professionals specifically, UTEP&apos;s connections to the local market are particularly valuable. Faculty members in the finance and real estate programs frequently conduct research on El Paso&apos;s housing market, commercial property trends, and cross-border real estate dynamics. Their findings are often shared through public lectures, published reports, and media commentary, providing practitioners with data-driven insights that can inform investment decisions and business strategy.
          </p>
          <p>
            The university&apos;s <strong>Hunt Institute for Global Competitiveness</strong> conducts research on border economics, international trade, and regional competitiveness. Its reports and policy briefs are widely cited by business leaders, policymakers, and media outlets, and they provide a rigorous analytical foundation for understanding the economic forces that shape El Paso&apos;s business environment. Entrepreneurs and investors who stay informed about the Hunt Institute&apos;s work gain a strategic advantage in anticipating market shifts and identifying emerging opportunities.
          </p>
          <p>
            UTEP&apos;s alumni network is another powerful resource. With more than 130,000 living alumni, many of whom remain in the El Paso region, the university&apos;s graduates form a vast professional network that spans every industry and sector. Alumni events, mentorship programs, and online communities provide opportunities for current students and recent graduates to connect with established professionals, seek advice, and explore career opportunities. For business owners, hiring UTEP alumni means tapping into a network of professionals who understand the local market and are invested in the community&apos;s success.
          </p>
        </div>
      </section>

      {/* Final Image */}
      <section className="mb-20">
        <div className="rounded-2xl overflow-hidden h-[420px] w-full">
          <img
            src="https://readdy.ai/api/search-image?query=inspiring%20university%20graduation%20ceremony%20outdoors%20with%20graduates%20in%20caps%20and%20gowns%20celebrating%20throwing%20caps%20in%20the%20air%20with%20dramatic%20desert%20mountain%20backdrop%20and%20golden%20sunset%20light%20creating%20a%20warm%20celebratory%20atmosphere&width=1100&height=420&seq=utepgrad010&orientation=landscape"
            alt="UTEP Graduation Ceremony"
            className="w-full h-full object-cover object-top"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-2xl p-12 text-center text-white mb-4">
        <h2 className="text-4xl font-bold mb-6">Explore Business Opportunities in El Paso</h2>
        <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
          Whether you&apos;re considering a degree at UTEP, investing in real estate near campus, or launching a business that serves the university community, El Paso offers a wealth of opportunity. Connect with us to learn more.
        </p>
        <div className="flex items-center justify-center space-x-4 flex-wrap gap-y-3">
          <a
            href="https://www.utep.edu/business/"
            target="_blank"
            rel="nofollow noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-[#D2691E] text-white rounded-lg font-semibold hover:bg-[#B8571A] transition-all whitespace-nowrap cursor-pointer"
          >
            Visit UTEP College of Business
            <i className="ri-external-link-line ml-2"></i>
          </a>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-[#2C5F4F] rounded-lg font-semibold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer"
          >
            Contact Us
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>
    </>
  );
}
